import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { LockKeyhole, Shield } from 'lucide-react';

import { AuthProvider, useAuth } from '../app/providers/AuthProvider';
import ErrorBoundary from '../shared/ui/ErrorBoundary';
import { setAuthErrorHandler } from '../shared/api/client';
import { Button, Card, Input } from '../shared/ui';
import { GlobalFX } from '../shared/ui/GlobalStyles';
import { theme } from '../shared/config/theme';
import AdminDashboardPage from './AdminDashboardPage';


function AdminProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: theme.gradBg }}>
        <Card padding="lg" className="max-w-md w-full">
          <div className="text-center">
            <div className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
              Загружаем admin.noesa.ai
            </div>
            <div className="text-sm" style={{ color: theme.textSoft }}>
              Проверяем текущую сессию.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}


function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuth();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(email, password);
    if (!result?.success) {
      setError(String(result?.error || 'Не удалось войти'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ background: theme.gradBg }}>
      <Card padding="lg" className="w-full max-w-lg">
        <div className="flex items-start gap-4 mb-6">
          <div
            className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(30,102,245,0.10)', color: theme.brand }}
          >
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: theme.text }}>
              Вход в admin.noesa.ai
            </h1>
            <p className="text-sm leading-6" style={{ color: theme.textSoft }}>
              Логин здесь отдельный не нужен: используется обычная авторизация noesa. После входа backend дополнительно проверит, что доступ разрешён только для owner-аккаунта.
            </p>
          </div>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="arthuroberud@gmail.com"
            autoComplete="username"
            fullWidth
          />
          <Input
            label="Пароль"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Пароль"
            autoComplete="current-password"
            fullWidth
            rightIcon={<LockKeyhole className="h-4 w-4" />}
          />

          {error && (
            <div className="text-sm" style={{ color: '#b91c1c' }}>
              {error}
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" loading={isLoading} disabled={!email || !password}>
              Войти
            </Button>
            <Button type="button" variant="outline" onClick={() => window.location.assign('https://noesa.ai')}>
              На основной сайт
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}


function AdminRoutes() {
  React.useEffect(() => {
    setAuthErrorHandler(() => {
      const p = window.location.pathname || '/';
      if (!p.startsWith('/login')) {
        window.location.assign('/login');
      }
    });
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/welcome" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<AdminLoginPage />} />
        <Route
          path="/"
          element={(
            <AdminProtectedRoute>
              <AdminDashboardPage />
            </AdminProtectedRoute>
          )}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}


export default function AdminApp() {
  const [queryClient] = React.useState(() => new QueryClient());

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <GlobalFX />
          <AdminRoutes />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
